import * as React from "react";
import { AlertCircle, Loader2, ReceiptText } from "lucide-react";
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Textarea,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import { ApiError } from "@eduviaos/shared";
import {
  EXPENSE_CATEGORY_LABELS,
  PAYMENT_METHOD_LABELS,
  monthDateBounds,
  paiseToRupees,
  rupeesToPaise,
  useCreateExpense,
  useSuggestExpense,
  useUpdateExpense,
  type CreateExpenseBody,
  type Expense,
  type ExpenseCategory,
  type PaymentMethod,
  type UpdateExpenseBody,
} from "@/lib/accounting/api";

/**
 * Phase SC-2b — expense create / edit drawer.
 *
 * Right-side sheet mirroring `<ZoneEditorSheet>`: the ledger `<DataTable>`
 * stays visible behind while the owner types. Amount is entered in
 * RUPEES and converted to integer paise at submit only — the wire and BE
 * are paise throughout. Edit mode PATCHes only changed fields; a zero-diff
 * save closes silently.
 */

interface DraftState {
  title: string;
  category: ExpenseCategory;
  customCategoryLabel: string;
  amountRupees: string;
  incurredOn: string; // yyyy-mm-dd
  vendorName: string;
  paymentMethod: PaymentMethod | "__none__";
  note: string;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function EMPTY_DRAFT(): DraftState {
  return {
    title: "",
    category: "rent",
    customCategoryLabel: "",
    amountRupees: "",
    incurredOn: todayIso(),
    vendorName: "",
    paymentMethod: "__none__",
    note: "",
  };
}

function draftFromExisting(existing: Expense | null | undefined): DraftState {
  if (!existing) return EMPTY_DRAFT();
  return {
    title: existing.title,
    category: existing.category,
    customCategoryLabel: existing.custom_category_label ?? "",
    amountRupees: String(paiseToRupees(existing.amount_paise)),
    incurredOn: existing.incurred_on.slice(0, 10),
    vendorName: existing.vendor_name ?? "",
    paymentMethod: existing.payment_method ?? "__none__",
    note: existing.note ?? "",
  };
}

function trimmedOrNull(v: string): string | null {
  const t = v.trim();
  return t.length > 0 ? t : null;
}

export interface ExpenseEditorSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existing?: Expense | null;
  /** New costs are posted only inside this calendar month. */
  lockedMonth?: { year: number; month: number } | null;
  onSaved?: (saved: Expense) => void;
}

export function ExpenseEditorSheet({
  open,
  onOpenChange,
  existing,
  lockedMonth,
  onSaved,
}: ExpenseEditorSheetProps) {
  const isEdit = Boolean(existing);
  const [draft, setDraft] = React.useState<DraftState>(() => draftFromExisting(existing));
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      const next = draftFromExisting(existing);
      if (!existing && lockedMonth) {
        const bounds = monthDateBounds(lockedMonth.year, lockedMonth.month);
        const today = todayIso();
        next.incurredOn = today >= bounds.min && today <= bounds.max ? today : bounds.min;
      }
      setDraft(next);
      setSubmitError(null);
    }
  }, [open, existing, lockedMonth]);

  const createMutation = useCreateExpense();
  const updateMutation = useUpdateExpense();
  const suggestMutation = useSuggestExpense();
  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const set = <K extends keyof DraftState>(key: K, value: DraftState[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const suggest = async () => {
    if (draft.title.trim().length === 0) return;
    setSubmitError(null);
    try {
      const result = await suggestMutation.mutateAsync({
        title: draft.title,
        note: draft.note,
        kind: "one_time",
      });
      setDraft((prev) => ({
        ...prev,
        category: result.category,
        customCategoryLabel: result.custom_category_label ?? "",
      }));
      toast.success(result.rationale);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Couldn't suggest a category.";
      setSubmitError(message);
    }
  };

  const dateBounds = lockedMonth ? monthDateBounds(lockedMonth.year, lockedMonth.month) : null;
  const amountPaise = rupeesToPaise(draft.amountRupees);
  const customLabel = draft.category === "other" ? trimmedOrNull(draft.customCategoryLabel) : null;
  const canSubmit =
    draft.title.trim().length > 0 &&
    amountPaise !== null &&
    amountPaise > 0 &&
    (draft.category !== "other" || customLabel !== null) &&
    !isSubmitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!canSubmit || amountPaise === null) return;

    const payment = draft.paymentMethod === "__none__" ? null : draft.paymentMethod;
    const incurredIso = new Date(`${draft.incurredOn}T00:00:00Z`).toISOString();

    try {
      if (existing) {
        const body: UpdateExpenseBody = {};
        const title = draft.title.trim();
        if (title !== existing.title) body.title = title;
        if (draft.category !== existing.category) body.category = draft.category;
        if (customLabel !== (existing.custom_category_label ?? null))
          body.custom_category_label = customLabel;
        if (amountPaise !== existing.amount_paise) body.amount_paise = amountPaise;
        if (incurredIso.slice(0, 10) !== existing.incurred_on.slice(0, 10))
          body.incurred_on = incurredIso;
        if (trimmedOrNull(draft.vendorName) !== (existing.vendor_name ?? null))
          body.vendor_name = trimmedOrNull(draft.vendorName);
        if (payment !== (existing.payment_method ?? null)) body.payment_method = payment;
        if (trimmedOrNull(draft.note) !== (existing.note ?? null))
          body.note = trimmedOrNull(draft.note);

        if (Object.keys(body).length === 0) {
          onOpenChange(false);
          return;
        }
        const saved = await updateMutation.mutateAsync({ id: existing.id, body });
        onSaved?.(saved);
        toast.success(`Saved changes to ${saved.title}.`);
        onOpenChange(false);
      } else {
        const body: CreateExpenseBody = {
          title: draft.title.trim(),
          category: draft.category,
          custom_category_label: customLabel,
          amount_paise: amountPaise,
          incurred_on: incurredIso,
          recurrence: "one_time",
          vendor_name: trimmedOrNull(draft.vendorName),
          payment_method: payment,
          note: trimmedOrNull(draft.note),
        };
        const saved = await createMutation.mutateAsync(body);
        onSaved?.(saved);
        toast.success(`Logged expense ${saved.title}.`);
        onOpenChange(false);
      }
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message || "Couldn't save the expense. Please retry."
          : err instanceof Error
            ? err.message
            : "Couldn't save the expense. Please retry.";
      setSubmitError(message);
    }
  };

  const title = isEdit ? "Edit expense" : "Log an expense";
  const description = existing?.recurring_expense_id
    ? "This row was posted by a recurring cost. Saving changes this month only."
    : isEdit
      ? `Update ${existing?.title ?? "this expense"}.`
      : "Record a one-time cost for this month — renovation, casual labour, or anything else that should not repeat.";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[480px]"
        data-testid="expense-editor-sheet"
      >
        <SheetHeader className="border-border/60 border-b px-6 pb-4 pt-6">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="bg-primary/10 text-primary mt-0.5 grid size-10 shrink-0 place-items-center rounded-lg"
            >
              <ReceiptText className="size-5" />
            </span>
            <div className="flex-1">
              <SheetTitle className="tracking-tight">{title}</SheetTitle>
              <SheetDescription className="mt-1 text-xs">{description}</SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            {submitError ? (
              <div
                role="alert"
                className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
              >
                <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
                <span className="flex-1">{submitError}</span>
              </div>
            ) : null}

            <Field label="Title" htmlFor="exp-title" required>
              <div className="flex gap-2">
                <Input
                  id="exp-title"
                  value={draft.title}
                  maxLength={200}
                  required
                  autoFocus
                  onChange={(e) => set("title", e.target.value)}
                  placeholder="Hall renovation, casual labour…"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={draft.title.trim().length === 0 || suggestMutation.isPending}
                  onClick={() => void suggest()}
                >
                  Suggest
                </Button>
              </div>
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Category" htmlFor="exp-category" required>
                <Select
                  value={draft.category}
                  onValueChange={(v) => {
                    const category = v as ExpenseCategory;
                    setDraft((prev) => ({
                      ...prev,
                      category,
                      customCategoryLabel: category === "other" ? prev.customCategoryLabel : "",
                    }));
                  }}
                >
                  <SelectTrigger id="exp-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(EXPENSE_CATEGORY_LABELS) as ExpenseCategory[]).map((c) => (
                      <SelectItem key={c} value={c}>
                        {EXPENSE_CATEGORY_LABELS[c]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Amount (₹)" htmlFor="exp-amount" required>
                <Input
                  id="exp-amount"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  value={draft.amountRupees}
                  required
                  onChange={(e) => set("amountRupees", e.target.value)}
                  placeholder="12500"
                />
              </Field>
            </div>

            {draft.category === "other" ? (
              <Field
                label="What is this cost?"
                htmlFor="exp-custom-category"
                required
                helper="Shown on the profit and loss breakdown."
              >
                <Input
                  id="exp-custom-category"
                  value={draft.customCategoryLabel}
                  maxLength={80}
                  required
                  onChange={(e) => set("customCategoryLabel", e.target.value)}
                  placeholder="Tea, printing, generator diesel…"
                />
              </Field>
            ) : null}

            <Field
              label="Date incurred"
              htmlFor="exp-date"
              required
              helper={
                dateBounds ? "This cost is counted only in the month you are viewing." : undefined
              }
            >
              <Input
                id="exp-date"
                type="date"
                min={dateBounds?.min}
                max={dateBounds?.max}
                value={draft.incurredOn}
                required
                onChange={(e) => set("incurredOn", e.target.value)}
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Paid to" htmlFor="exp-vendor">
                <Input
                  id="exp-vendor"
                  value={draft.vendorName}
                  maxLength={200}
                  onChange={(e) => set("vendorName", e.target.value)}
                  placeholder="State Electricity Board"
                />
              </Field>

              <Field label="Payment method" htmlFor="exp-payment">
                <Select
                  value={draft.paymentMethod}
                  onValueChange={(v) => set("paymentMethod", v as DraftState["paymentMethod"])}
                >
                  <SelectTrigger id="exp-payment">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Not specified</SelectItem>
                    {(Object.keys(PAYMENT_METHOD_LABELS) as PaymentMethod[]).map((m) => (
                      <SelectItem key={m} value={m}>
                        {PAYMENT_METHOD_LABELS[m]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field label="Note" htmlFor="exp-note">
              <Textarea
                id="exp-note"
                value={draft.note}
                maxLength={2000}
                rows={2}
                onChange={(e) => set("note", e.target.value)}
                placeholder="Optional — invoice number, context, etc."
              />
            </Field>
          </div>

          <div className="border-border/60 bg-card/40 flex items-center justify-end gap-2 border-t px-6 py-4 backdrop-blur">
            <SheetClose asChild>
              <Button variant="ghost" type="button" disabled={isSubmitting}>
                Cancel
              </Button>
            </SheetClose>
            <Button type="submit" disabled={!canSubmit}>
              {isSubmitting ? (
                <Loader2 aria-hidden="true" className={cn("size-4 animate-spin")} />
              ) : null}
              {isEdit ? "Save changes" : "Log expense"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

interface FieldProps {
  label: string;
  htmlFor: string;
  required?: boolean;
  helper?: string;
  children: React.ReactNode;
}

function Field({ label, htmlFor, required, helper, children }: FieldProps) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="text-xs">
        {label}
        {required ? <span className="text-destructive ml-0.5">*</span> : null}
      </Label>
      <div className="mt-1">{children}</div>
      {helper ? <p className="text-muted-foreground mt-1 text-xs">{helper}</p> : null}
    </div>
  );
}
